import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Camera, Loader2, Maximize2 } from "lucide-react";
import { InfoHint } from "../ui/InfoHint";
import { formatMoney } from "../../utils/formatters";
import {
  FAR_FROM_ADDRESS_METERS,
  codMethodMap,
  codStatusMap,
  formatDistance,
  qualityTone,
} from "./resolution";

// Franja de resolución del detalle de pedido.
//
// Responde la pregunta que el operador trae según el estado del pedido, sin
// scroll: ¿se entregó y lo puedo probar?, ¿por qué falló?, ¿quién lo tiene?
// El contenido sale de `buildResolution` (resolution.js); las acciones llegan ya
// filtradas por permisos y ordenadas, la primera es la principal.

const MAX_VISIBLE_PHOTOS = 3;

const formatDate = (iso) => (iso ? moment(iso).format("DD/MM/YYYY HH:mm") : null);

function PhotoStrip({ photos, onOpen, t }) {
  const visible = photos.slice(0, MAX_VISIBLE_PHOTOS);
  const hidden = photos.length - visible.length;
  return (
    <div className="pd-summary__photos">
      {visible.map((src, index) => {
        const isLastWithMore = hidden > 0 && index === visible.length - 1;
        return (
          <button
            key={src || index}
            type="button"
            className="pd-pod__photo"
            onClick={() => onOpen?.(photos, index)}
            aria-label={t("Ampliar foto {{current}} de {{total}}", {
              current: index + 1,
              total: photos.length,
            })}
          >
            <img src={src} alt={t("Evidencia {{n}}", { n: index + 1 })} />
            {isLastWithMore ? (
              <span className="pd-summary__more" aria-hidden="true">+{hidden}</span>
            ) : (
              <span className="pd-pod__photo-zoom"><Maximize2 size={14} /></span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function DistanceLine({ distance, prefix, onOpenMap, t, locale }) {
  if (!distance && !onOpenMap) return null;
  return (
    <div className="pd-summary__fact pd-summary__fact--row">
      {distance && (
        <>
          <span>{t(prefix, { distance: formatDistance(distance.meters, locale) })}</span>
          {distance.far && <span className="pd-pill pd-pill--warning">{t("Lejos")}</span>}
          <InfoHint
            label={t(
              "Distancia en línea recta entre el punto que registró el repartidor y la dirección de entrega verificada. Sobre {{m}} m se marca como «lejos».",
              { m: FAR_FROM_ADDRESS_METERS },
            )}
            srLabel={t("Cómo se calcula la distancia")}
          />
        </>
      )}
      {onOpenMap && (
        <button type="button" className="pd-link pd-summary__map-link" onClick={onOpenMap}>
          {t("Ver en el mapa")}
        </button>
      )}
    </div>
  );
}

function CodLine({ cod, t }) {
  if (!cod) return null;
  const meta = codStatusMap[cod.status];
  if (cod.status === "cancelled") {
    return <div className="pd-summary__fact">{t(meta.label)}</div>;
  }
  const collected = cod.status === "collected";
  const amount = collected ? cod.collectedAmount ?? cod.amount : cod.amount;
  return (
    <div className="pd-summary__fact pd-summary__fact--row">
      <span>
        {t(meta?.label || "Por cobrar")}{" "}
        <span className="pd-summary__strong">{formatMoney(amount, cod.currency)}</span>
      </span>
      {collected && cod.method && (
        <span className="pd-summary__muted">{t(codMethodMap[cod.method] || cod.method)}</span>
      )}
    </div>
  );
}

function QualityLine({ quality, t }) {
  if (quality === "none") {
    return (
      <div className="pd-summary__fact pd-summary__fact--row">
        <span>{t("Calidad de la evidencia")}</span>
        <span className="pd-summary__muted">{t("IA analizando…")}</span>
      </div>
    );
  }
  if (!quality || quality.totalScore == null) return null;
  const score = Math.round(quality.totalScore);
  return (
    <div className="pd-summary__fact pd-summary__fact--row">
      <span>{t("Calidad de la evidencia")}</span>
      <span className={`pd-pill pd-pill--${qualityTone(quality.totalScore)}`}>{score} / 100</span>
    </div>
  );
}

function Facts({ resolution, context, t, locale }) {
  const { kind, receiver, failure, distance, returnReason, cod } = resolution;
  const { driverName, sellerName, address, quality, onOpenEventMap } = context;

  switch (kind) {
    case "delivered":
      return (
        <>
          <div className="pd-summary__fact">
            {receiver?.name ? (
              <>{t("Recibió")} <span className="pd-summary__strong">{receiver.name}</span></>
            ) : (
              <span className="pd-summary__muted">{t("Sin nombre del receptor")}</span>
            )}
          </div>
          <div className="pd-summary__fact pd-summary__muted">
            {receiver?.doc ? `${t("RUT")} ${receiver.doc}` : t("RUT no informado")}
          </div>
          <DistanceLine
            distance={distance}
            prefix="A {{distance}} de la dirección de entrega"
            onOpenMap={onOpenEventMap}
            t={t}
            locale={locale}
          />
          <QualityLine quality={quality} t={t} />
          <CodLine cod={cod} t={t} />
        </>
      );
    case "failed":
      return (
        <>
          <div className="pd-summary__fact pd-summary__strong">
            {failure?.reason ? t(failure.reason) : t("Sin motivo registrado")}
          </div>
          {failure?.options?.length > 0 && (
            <div className="pd-timeline__chips">
              {failure.options.map((option) => (
                <span key={option} className="pd-timeline__chip">{option}</span>
              ))}
            </div>
          )}
          {failure?.note && <div className="pd-summary__note">“{failure.note}”</div>}
          <DistanceLine
            distance={distance}
            prefix="Intento a {{distance}} de la dirección de entrega"
            onOpenMap={onOpenEventMap}
            t={t}
            locale={locale}
          />
          <CodLine cod={cod} t={t} />
        </>
      );
    case "in_route":
      return (
        <>
          {address?.line && <div className="pd-summary__fact">{address.line}</div>}
          {address?.sub && <div className="pd-summary__fact pd-summary__muted">{address.sub}</div>}
          {address?.instructions && (
            <div className="pd-summary__fact">
              {t("Instrucciones")}: {address.instructions}
            </div>
          )}
          <CodLine cod={cod} t={t} />
        </>
      );
    case "warehouse":
    case "pending":
      return (
        <>
          {kind === "pending" && sellerName && (
            <div className="pd-summary__fact">
              {t("Vendedor")}: <span className="pd-summary__strong">{sellerName}</span>
            </div>
          )}
          <div className="pd-summary__fact">
            {driverName ? (
              <>{t("Repartidor")}: <span className="pd-summary__strong">{driverName}</span></>
            ) : (
              <span className="pd-summary__muted">{t("Sin repartidor asignado")}</span>
            )}
          </div>
          <CodLine cod={cod} t={t} />
        </>
      );
    case "return":
      return returnReason ? (
        <div className="pd-summary__fact">
          {t("Motivo devolución:")} {t(returnReason)}
        </div>
      ) : null;
    default:
      return null;
  }
}

function metaLine(resolution, context, t) {
  const { kind, at, by, attempt } = resolution;
  const date = formatDate(at);
  if (kind === "in_route") {
    const driver = context.driverName || by;
    if (date && driver) return t("Desde {{date}} con {{name}}", { date, name: driver });
    return date ? t("Desde {{date}}", { date }) : null;
  }
  const parts = [];
  if (kind === "failed" && attempt) parts.push(t("Intento {{n}}", { n: attempt }));
  if (date) parts.push(date);
  if (by) parts.push(t("Por {{name}}", { name: by }));
  return parts.length ? parts.join(" · ") : null;
}

function ActionButton({ action, primary }) {
  const className = `pd-summary__btn${primary ? " pd-summary__btn--primary" : ""}`;
  const content = (
    <>
      {action.busy ? <Loader2 size={14} className="pd-btn-ghost__spinner" aria-hidden="true" /> : action.icon}
      <span>{action.label}</span>
    </>
  );
  if (action.href) {
    return (
      <a className={className} href={action.href} title={action.title}>
        {content}
      </a>
    );
  }
  return (
    <button
      type="button"
      className={className}
      onClick={action.onClick}
      disabled={action.busy}
      aria-busy={action.busy || undefined}
      title={action.title}
    >
      {content}
    </button>
  );
}

export function ResolutionSummary({
  resolution,
  statusLabel,
  statusClass,
  stacked = false,
  context = {},
  actions = [],
  onOpenPhotos,
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language;
  const meta = metaLine(resolution, context, t);
  const showNoPhoto = resolution.kind === "delivered" && resolution.photos.length === 0;

  return (
    <section
      className={`pd-summary${stacked ? " pd-summary--stacked" : ""}`}
      aria-label={t("Resumen del pedido")}
    >
      {/* Apilado (ficha de parada de live-routes) la ficha ya muestra estado,
          hora e intentos arriba: repetirlos acá sería la tercera vez. */}
      {!stacked && (
        <div className="pd-summary__head">
          <span className={`status-badge status-badge--${statusClass}`}>{statusLabel}</span>
          {meta && <span className="pd-summary__meta">{meta}</span>}
        </div>
      )}

      <div className="pd-summary__body">
        {resolution.photos.length > 0 && (
          <PhotoStrip photos={resolution.photos} onOpen={onOpenPhotos} t={t} />
        )}
        {showNoPhoto && (
          <div className="pd-summary__no-photo">
            <Camera size={20} aria-hidden="true" />
            <span>{t("Entrega confirmada sin foto")}</span>
          </div>
        )}

        <div className="pd-summary__facts">
          <Facts resolution={resolution} context={context} t={t} locale={locale} />
        </div>

        {actions.length > 0 && (
          <div className="pd-summary__actions">
            {actions.map((action, index) => (
              <ActionButton key={action.id} action={action} primary={index === 0} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default ResolutionSummary;
